import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useId } from 'react';

import { useKhoaCuonTrang } from './khoaCuonTrang.js';

/** Bề ngang tối đa của hộp. `md` (576px) là mặc định cho form sửa dữ liệu. */
export const BE_RONG_HOP = {
  sm: 'sm:max-w-md', // 448px — hộp một hai ô nhập
  md: 'sm:max-w-xl', // 576px — form sửa dữ liệu
  lg: 'sm:max-w-2xl', // 672px — form dài hoặc có danh sách
  xl: 'sm:max-w-3xl', // 768px — hộp có bảng bên trong
} as const;

export type BeRongHop = keyof typeof BE_RONG_HOP;

export interface HopThoaiProps {
  /** Tiêu đề chữ. Bỏ qua khi truyền `dauHop`, nhưng vẫn dùng cho `aria-label`. */
  title: string;
  /**
   * Đầu hộp tuỳ biến (avatar + tên + email…). Mặc định chỉ in `title`.
   * Nút đóng X do `HopThoai` vẽ, đừng tự thêm.
   */
  dauHop?: ReactNode;
  /** Thân hộp — phần **duy nhất** được cuộn. */
  children: ReactNode;
  /** Chân hộp, thường là Huỷ / Lưu. Luôn nhìn thấy. */
  chanHop?: ReactNode;
  rong?: BeRongHop;
  onDong: () => void;
}

/**
 * Hộp thoại dùng chung: ba phần **đầu / thân / chân**, cao tối đa `90vh`.
 *
 * Vì sao cần: trước đây mỗi hộp tự dựng `fixed inset-0 … p-6` mà **không** đặt
 * chiều cao tối đa và **không** khoá cuộn trang. Trên laptop cao ~900px, hộp
 * "Sửa thành viên" cao hơn màn hình nên phần đầu bị cắt, nút Huỷ/Lưu khuất
 * dưới, và cuộn chuột làm trang nền trượt theo để lộ thẻ phía sau.
 *
 * Ở đây **chỉ thân hộp cuộn**: đầu hộp (tên người, nút X) và chân hộp (Huỷ/Lưu)
 * luôn nhìn thấy dù thân dài bao nhiêu. Trang nền bị khoá cuộn
 * (`useKhoaCuonTrang`), đếm theo số hộp đang mở nên hộp lồng nhau vẫn đúng.
 *
 * Trên điện thoại hộp chiếm gần hết màn hình (`w-full` cộng `p-4` của lớp phủ);
 * `BE_RONG_HOP` chỉ kẹp bề ngang **từ `sm` trở lên**.
 *
 * Phím `Esc` đóng hộp. Bấm ra ngoài cũng đóng — lớp phủ là một `<button>` để
 * bàn phím và trình đọc màn hình đều thấy.
 */
export function HopThoai({ title, dauHop, children, chanHop, rong = 'md', onDong }: HopThoaiProps) {
  const idTieuDe = useId();
  useKhoaCuonTrang();

  useEffect(() => {
    function khiBamPhim(e: KeyboardEvent) {
      if (e.key === 'Escape') onDong();
    }
    document.addEventListener('keydown', khiBamPhim);
    return () => {
      document.removeEventListener('keydown', khiBamPhim);
    };
  }, [onDong]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Đóng"
        className="absolute inset-0 bg-black/30"
        onClick={onDong}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTieuDe}
        className={`relative flex max-h-[90vh] w-full flex-col overflow-hidden rounded-the border border-vien bg-be-mat text-left ${BE_RONG_HOP[rong]}`}
      >
        <div className="flex shrink-0 items-start gap-3 border-b border-vien px-6 py-4">
          <div className="min-w-0 flex-1" id={idTieuDe}>
            {dauHop ?? <h2 className="truncate text-tieu-de-hop font-bold text-chu">{title}</h2>}
          </div>

          <button
            type="button"
            aria-label="Đóng hộp thoại"
            onClick={onDong}
            className="vung-bam -mt-1 -mr-2 shrink-0 rounded-[10px] px-2 text-chu-mo hover:bg-neutral-nen hover:text-chu"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        {/* Phần duy nhất cuộn. `min-h-0` để flex child co được trong hộp cao cố định. */}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>

        {chanHop && (
          <div className="shrink-0 border-t border-vien px-6 py-4">
            <div className="flex flex-wrap justify-end gap-3">{chanHop}</div>
          </div>
        )}
      </div>
    </div>
  );
}
